import { useEffect, useState } from 'react';
import {
  Archive,
  ArrowRight,
  Calendar,
  HelpCircle,
  Sparkles,
  TrendingUp,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageSeo } from './PageSeo';
import { NarrowPage, PageHeader } from './SiteChrome';
import { SiteFooter } from './SiteFooter';
import { PAGE_META } from '../lib/seo';
import { NEWS_ITEMS, getNewsUnreadCount, NEWS_SEEN_KEY } from '../lib/news';
import { supabase } from '../lib/supabase';
import { formatLocalYmd, shiftYmd } from '../lib/dates';
import { archivePath, fetchArchiveIndex } from '../lib/archivePaths';
import { fetchTopicPlayCounts } from '../lib/topicPlays';

export type TopicCard = {
  id: string;
  title: string;
  play_date: string;
  description?: string | null;
};

function formatShortDate(dateStr: string) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

export function Home({
  initialTodayTopic = null,
  initialYesterdayTopic = null,
  initialPopular = [],
}: {
  initialTodayTopic?: TopicCard | null;
  initialYesterdayTopic?: TopicCard | null;
  initialPopular?: TopicCard[];
} = {}) {
  const [newsUnreadCount, setNewsUnreadCount] = useState(0);
  const [todayTopic, setTodayTopic] = useState<TopicCard | null>(initialTodayTopic);
  const [yesterdayTopic, setYesterdayTopic] = useState<TopicCard | null>(initialYesterdayTopic);
  const [popular, setPopular] = useState<TopicCard[]>(initialPopular);
  const today = new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

  useEffect(() => {
    try {
      setNewsUnreadCount(getNewsUnreadCount(localStorage.getItem(NEWS_SEEN_KEY)));
    } catch {
      setNewsUnreadCount(0);
    }
  }, []);

  useEffect(() => {
    const todayYmd = formatLocalYmd();
    const yesterdayYmd = shiftYmd(todayYmd, -1);

    async function load() {
      const [{ data: latest }, counts] = await Promise.all([
        supabase
          .from('topics')
          .select('id, title, play_date, description')
          .in('play_date', yesterdayYmd ? [todayYmd, yesterdayYmd] : [todayYmd]),
        fetchTopicPlayCounts(),
        fetchArchiveIndex(),
      ]);

      const rows = (latest ?? []) as TopicCard[];
      setTodayTopic(rows.find((t) => t.play_date === todayYmd) ?? null);
      setYesterdayTopic(rows.find((t) => t.play_date === yesterdayYmd) ?? null);

      const ranked = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
      const topIds = ranked.slice(0, 12).map((entry) => entry[0]);
      if (topIds.length === 0) {
        setPopular([]);
        return;
      }

      const { data: popularRows } = await supabase
        .from('topics')
        .select('id, title, play_date, description')
        .in('id', topIds)
        .lt('play_date', todayYmd);

      const byId = new Map((popularRows ?? []).map((t) => [t.id, t as TopicCard]));
      const next: TopicCard[] = [];
      for (let i = 0; i < ranked.length && next.length < 5; i++) {
        const topic = byId.get(ranked[i][0]);
        if (!topic) continue;
        next.push(topic);
      }
      setPopular(next);
    }

    void load();
  }, []);

  const latestNews = NEWS_ITEMS.slice(0, 3);

  return (
    <NarrowPage>
    <div className="relative pb-8">
      <PageSeo meta={PAGE_META.home} />
      <PageHeader newsUnreadCount={newsUnreadCount} />

      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-[#22d3ee]/10 blur-3xl" />
        <div className="absolute top-[28rem] -right-32 w-80 h-80 rounded-full bg-[#facc15]/10 blur-3xl" />
        <div className="absolute top-[70rem] left-0 w-64 h-64 rounded-full bg-[#4ade80]/[0.07] blur-3xl" />
      </div>

      <section className="text-center pt-2 pb-4">
        <div className="text-[11px] font-black uppercase tracking-[0.25em] text-foreground/40 mb-5">
          {today}
        </div>
        <img
          src="/centumth logo black.png"
          alt="Centumth"
          className="h-20 w-auto mx-auto mb-6 dark:hidden"
        />
        <img
          src="/centumth logo white.png"
          alt="Centumth"
          className="h-20 w-auto mx-auto mb-6 hidden dark:block"
        />
        <p className="text-lg text-foreground/60 max-w-sm mx-auto leading-relaxed">
          One topic. A hidden Top 100.
          <br />
          Guess the answers closest to <span className="font-bold text-foreground">#100</span>.
        </p>
        <div className="w-full max-w-[220px] mx-auto mt-5 mb-10">
          <div className="h-1.5 rounded-full bg-[linear-gradient(to_right,#fb923c,#facc15,#4ade80,#22d3ee)]" />
          <div className="flex justify-between mt-1.5 text-[9px] font-black uppercase tracking-widest text-foreground/35">
            <span>#1 Obvious</span>
            <span>#100 Hidden gem</span>
          </div>
        </div>

        <div className="flex flex-col gap-3 w-full max-w-xs mx-auto">
          <Link
            to="/play"
            className="group w-full bg-foreground text-background py-4 rounded-2xl font-bold text-lg hover:opacity-90 transition-all active:scale-[0.98] shadow-lg shadow-foreground/10 flex items-center justify-center gap-2"
          >
            Play today’s game
            <ArrowRight size={20} className="transition-transform group-hover:translate-x-0.5" />
          </Link>
          <Link
            to="/archive"
            className="w-full border border-border py-3.5 rounded-2xl font-bold hover:bg-muted transition-colors flex items-center justify-center gap-2"
          >
            <Archive size={18} className="text-foreground/70" />
            Pick a previous game
          </Link>
        </div>
        <div className="text-xs text-foreground/40 mt-6">A new topic drops every midnight</div>
      </section>

      <section className="mt-20">
        <div className="text-[11px] font-black uppercase tracking-[0.25em] text-foreground/40 mb-3">
          How it works
        </div>
        <div className="rounded-3xl border border-border bg-muted/30 p-5 sm:p-6">
          <h2 className="text-2xl font-black tracking-tight mb-3">Five guesses. Chase #100.</h2>
          <p className="text-[15px] leading-relaxed text-foreground/70 mb-5">
            Every day has a hidden Top 100. Obvious answers sit near #1 and score almost nothing.
            The hidden gems — the ones most people miss — sit near #100 and are worth the most.
          </p>
          <div className="grid grid-cols-3 gap-2 mb-5">
            <div className="rounded-2xl border border-border bg-background/70 px-3 py-3">
              <div className="text-lg font-black tabular-nums">1</div>
              <div className="text-[11px] font-bold text-foreground/50 leading-snug mt-0.5">
                One topic a day
              </div>
            </div>
            <div className="rounded-2xl border border-border bg-background/70 px-3 py-3">
              <div className="text-lg font-black tabular-nums">5</div>
              <div className="text-[11px] font-bold text-foreground/50 leading-snug mt-0.5">
                Guesses to rank
              </div>
            </div>
            <div className="rounded-2xl border border-border bg-background/70 px-3 py-3">
              <div className="text-lg font-black tabular-nums">#100</div>
              <div className="text-[11px] font-bold text-foreground/50 leading-snug mt-0.5">
                Hidden gems win
              </div>
            </div>
          </div>
          <div className="h-1.5 rounded-full bg-[linear-gradient(to_right,#fb923c,#facc15,#4ade80,#22d3ee)] mb-5" />
          <Link
            to="/how-to-play"
            className="inline-flex items-center gap-2 bg-foreground text-background px-4 py-2.5 rounded-xl font-bold text-sm hover:opacity-90 transition-opacity"
          >
            <HelpCircle size={16} />
            How to play
          </Link>
        </div>
      </section>

      <section className="mt-20">
        <div className="text-[11px] font-black uppercase tracking-[0.25em] text-foreground/40 mb-3">
          Latest games
        </div>
        <h2 className="text-2xl font-black tracking-tight mb-4">Jump in</h2>
        <div className="space-y-3">
          <Link
            to="/play"
            className="group block rounded-3xl border border-border bg-foreground text-background px-5 py-5 hover:opacity-90 transition-opacity"
          >
            <div className="text-[10px] font-black uppercase tracking-widest opacity-60 mb-1">
              Today’s Top 100
            </div>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-lg font-black leading-snug">
                  {todayTopic?.title ?? 'Play today’s list'}
                </div>
                {todayTopic?.description && (
                  <div className="text-sm opacity-70 line-clamp-2 mt-1">{todayTopic.description}</div>
                )}
              </div>
              <div className="shrink-0 w-9 h-9 rounded-full bg-background/15 flex items-center justify-center">
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
              </div>
            </div>
          </Link>

          {yesterdayTopic && (
            <Link
              to={archivePath(yesterdayTopic)}
              className="group block rounded-3xl border border-border bg-background/60 px-5 py-5 hover:bg-muted/70 hover:border-foreground/20 transition-all"
            >
              <div className="text-[10px] font-black uppercase tracking-widest text-foreground/40 mb-1">
                Yesterday’s challenge
              </div>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-lg font-black leading-snug">{yesterdayTopic.title}</div>
                  <div className="text-sm text-foreground/50 mt-1">
                    {formatShortDate(yesterdayTopic.play_date)}
                  </div>
                </div>
                <div className="shrink-0 w-9 h-9 rounded-full border border-border bg-muted/50 group-hover:bg-foreground group-hover:text-background group-hover:border-foreground flex items-center justify-center transition-colors">
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
                </div>
              </div>
            </Link>
          )}

          <Link
            to="/archive"
            className="group flex items-center justify-between gap-3 rounded-3xl border border-dashed border-border px-5 py-4 hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-center gap-3 min-w-0">
              <Calendar size={18} className="text-foreground/50 shrink-0" />
              <div className="text-left">
                <div className="font-bold">Browse the full archive</div>
                <div className="text-sm text-foreground/50">Every past Top 100, free to replay</div>
              </div>
            </div>
            <ArrowRight
              size={16}
              className="shrink-0 text-foreground/40 transition-transform group-hover:translate-x-0.5"
            />
          </Link>
        </div>
      </section>

      <section className="mt-20">
        <div className="flex items-end justify-between gap-3 mb-4">
          <div>
            <div className="text-[11px] font-black uppercase tracking-[0.25em] text-foreground/40 mb-3">
              Popular games
            </div>
            <h2 className="text-2xl font-black tracking-tight">Most played</h2>
          </div>
          <Link
            to="/archive?sort=popular"
            className="shrink-0 text-xs font-bold uppercase tracking-wider text-foreground/50 hover:text-foreground transition-colors inline-flex items-center gap-1"
          >
            View all
            <ArrowRight size={12} />
          </Link>
        </div>
        {popular.length > 0 ? (
          <div className="space-y-3">
            {popular[0] && (
              <Link
                to={archivePath(popular[0])}
                className="group relative block overflow-hidden rounded-3xl border border-border bg-muted/30 px-5 py-6 hover:border-foreground/20 transition-all"
              >
                <div
                  aria-hidden
                  className="absolute inset-x-0 top-0 h-1.5 bg-[linear-gradient(to_right,#CD7F32,#facc15,#22d3ee)]"
                />
                <div className="flex items-start gap-4">
                  <div className="shrink-0 w-12 h-12 rounded-2xl font-black tabular-nums text-black bg-[linear-gradient(to_bottom,#CD7F32,#facc15,#22d3ee)] flex items-center justify-center">
                    1
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] font-black uppercase tracking-widest text-foreground/40 mb-1">
                      Community favorite
                    </div>
                    <div className="text-xl font-black leading-snug break-words">
                      {popular[0].title}
                    </div>
                    <div className="text-sm font-semibold text-foreground/50 mt-2">
                      {formatShortDate(popular[0].play_date)}
                    </div>
                  </div>
                  <div className="shrink-0 w-9 h-9 mt-1 rounded-full border border-border bg-background/70 group-hover:bg-foreground group-hover:text-background group-hover:border-foreground flex items-center justify-center transition-colors">
                    <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
                  </div>
                </div>
              </Link>
            )}
            {popular.slice(1).map((topic, i) => (
              <Link
                key={topic.id}
                to={archivePath(topic)}
                className="group flex items-center gap-3 sm:gap-4 rounded-3xl border border-border bg-background/60 px-4 py-4 hover:bg-muted/70 hover:border-foreground/20 transition-all"
              >
                <div className="shrink-0 w-10 h-10 rounded-2xl font-black tabular-nums bg-muted text-foreground/70 flex items-center justify-center">
                  {i + 2}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-bold leading-snug break-words">{topic.title}</div>
                  <div className="text-xs font-semibold text-foreground/45 mt-1">
                    {formatShortDate(topic.play_date)}
                  </div>
                </div>
                <div className="shrink-0 w-9 h-9 rounded-full border border-border bg-muted/50 group-hover:bg-foreground group-hover:text-background group-hover:border-foreground flex items-center justify-center transition-colors">
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="rounded-3xl border border-dashed border-border px-5 py-8 text-center">
            <div className="font-bold mb-1">Popular lists are warming up</div>
            <div className="text-sm text-foreground/50">
              Browse the archive while favorites start to surface.
            </div>
          </div>
        )}
        <Link
          to="/archive?sort=popular"
          className="mt-4 w-full border border-border rounded-2xl py-3.5 font-bold hover:bg-muted transition-colors flex items-center justify-center gap-2"
        >
          <TrendingUp size={16} />
          Browse archive by popularity
        </Link>
      </section>

      <section className="mt-20">
        <div className="flex items-end justify-between gap-3 mb-4">
          <div>
            <div className="text-[11px] font-black uppercase tracking-[0.25em] text-foreground/40 mb-3">
              Latest news
            </div>
            <h2 className="text-2xl font-black tracking-tight">What’s new</h2>
          </div>
          <Link
            to="/news"
            className="shrink-0 text-xs font-bold uppercase tracking-wider text-foreground/50 hover:text-foreground transition-colors inline-flex items-center gap-1"
          >
            All news
            <ArrowRight size={12} />
          </Link>
        </div>
        <div className="space-y-3">
          {latestNews.map((item) => (
            <Link
              key={item.id}
              to="/news"
              className="group block rounded-3xl border border-border bg-background/60 px-5 py-5 hover:bg-muted/70 hover:border-foreground/20 transition-all"
            >
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <Sparkles size={14} className="text-foreground/40" />
                <div className="text-[11px] font-black uppercase tracking-widest text-foreground/45">
                  {item.dateLabel}
                </div>
                {item.isNew && (
                  <div className="text-[10px] font-black uppercase tracking-widest bg-red-500 text-white px-2 py-0.5 rounded-full">
                    New
                  </div>
                )}
              </div>
              <div className="text-lg font-black leading-snug mb-1.5">{item.title}</div>
              <p className="text-sm text-foreground/60 leading-relaxed">{item.summary}</p>
            </Link>
          ))}
        </div>
      </section>

      <SiteFooter />
    </div>
    </NarrowPage>
  );
}
